/**
 * Offline store using localStorage for browser dev.
 * In production (Capacitor), this would use @capacitor-community/sqlite.
 * The interface stays the same — swap implementation later.
 */

import type { Plan, ProofOfPlayRecord, PresenceRecord } from '@/types';
import { logger } from './logger';

import { storageKey } from './storage-keys';

const KEYS = {
  PLAN: storageKey('cached_plan'),
  PLAN_VERSION: storageKey('plan_version'),
  PROOF_OF_PLAY: storageKey('proof_of_play_queue'),
  PRESENCE: storageKey('presence_queue'),
  LAST_SYNC: storageKey('last_sync'),
};

/**
 * Hard caps on telemetry queue size. localStorage has a per-origin cap
 * (~5 MB on most browsers), so unbounded growth here previously caused
 * silent quota errors and lost data. When the cap is hit we drop the
 * OLDEST records — preserving newer data is more useful for analytics.
 *
 * Numbers chosen so a 5-second-per-asset rotation can buffer ~7 hours of
 * proof-of-play offline before dropping anything.
 */
const MAX_PROOF_OF_PLAY_QUEUE = 5000;
const MAX_PRESENCE_QUEUE = 5000;

function getJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function setJSON(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

// ── Plan ─────────────────────────────────────────────────
//
// One-shot snapshot of the active schedule. Persisted so that on
// cold-start the player can render immediately from its last known
// good plan while sync runs in the background.

export function getCachedPlan(): Plan | null {
  return getJSON<Plan | null>(KEYS.PLAN, null);
}

export function setCachedPlan(plan: Plan): void {
  setJSON(KEYS.PLAN, plan);
  setJSON(KEYS.PLAN_VERSION, plan.planVersion);
}

export function getCachedPlanVersion(): string | null {
  return getJSON<string | null>(KEYS.PLAN_VERSION, null);
}

// ── Proof of Play Queue ──────────────────────────────────

export function getProofOfPlayQueue(): ProofOfPlayRecord[] {
  return getJSON(KEYS.PROOF_OF_PLAY, []);
}

export function addProofOfPlay(record: ProofOfPlayRecord): void {
  const queue = getProofOfPlayQueue();
  queue.push(record);
  if (queue.length > MAX_PROOF_OF_PLAY_QUEUE) {
    // Drop oldest entries beyond the cap. Logged so an operator can see
    // when sync has been failing long enough to lose data.
    const overflow = queue.length - MAX_PROOF_OF_PLAY_QUEUE;
    queue.splice(0, overflow);
    logger.warn('[OfflineStore] proof-of-play queue capped', {
      cap: MAX_PROOF_OF_PLAY_QUEUE,
      dropped: overflow,
    });
  }
  setJSON(KEYS.PROOF_OF_PLAY, queue);
}

export function clearSyncedProofOfPlay(): void {
  const queue = getProofOfPlayQueue().filter((r) => !r.synced);
  setJSON(KEYS.PROOF_OF_PLAY, queue);
}

export function markProofOfPlaySynced(ids: string[]): void {
  const queue = getProofOfPlayQueue();
  const idSet = new Set(ids);
  queue.forEach((r) => { if (idSet.has(r.id)) r.synced = true; });
  setJSON(KEYS.PROOF_OF_PLAY, queue);
}

// ── Presence Queue ───────────────────────────────────────

export function getPresenceQueue(): PresenceRecord[] {
  return getJSON(KEYS.PRESENCE, []);
}

export function addPresenceRecord(record: PresenceRecord): void {
  const queue = getPresenceQueue();
  queue.push(record);
  if (queue.length > MAX_PRESENCE_QUEUE) {
    const overflow = queue.length - MAX_PRESENCE_QUEUE;
    queue.splice(0, overflow);
    logger.warn('[OfflineStore] presence queue capped', {
      cap: MAX_PRESENCE_QUEUE,
      dropped: overflow,
    });
  }
  setJSON(KEYS.PRESENCE, queue);
}

export function markPresenceSynced(ids: string[]): void {
  const queue = getPresenceQueue();
  const idSet = new Set(ids);
  queue.forEach((r) => { if (idSet.has(r.id)) r.synced = true; });
  setJSON(KEYS.PRESENCE, queue);
}

export function clearSyncedPresence(): void {
  const queue = getPresenceQueue().filter((r) => !r.synced);
  setJSON(KEYS.PRESENCE, queue);
}

// ── Sync timestamp ───────────────────────────────────────

export function getLastSyncTime(): number {
  return getJSON(KEYS.LAST_SYNC, 0);
}

export function setLastSyncTime(ts: number): void {
  setJSON(KEYS.LAST_SYNC, ts);
}
